import React, { useState, useEffect, useMemo } from 'react';

/**
 * ForensicWatermark — Semi-transparent per-session identifier overlay.
 *
 * Renders a nearly invisible (2% opacity) session ID text that repositions
 * randomly every 30 seconds. This allows forensic identification of the
 * session if a screen recording is leaked.
 *
 * Validates: Req 1.32
 */
interface ForensicWatermarkProps {
  /** Optional session ID override (defaults to crypto.randomUUID()) */
  sessionId?: string;
}

export const ForensicWatermark: React.FC<ForensicWatermarkProps> = ({ sessionId }) => {
  const id = useMemo(() => sessionId || crypto.randomUUID(), [sessionId]);

  const [position, setPosition] = useState(() => getRandomPosition());

  useEffect(() => {
    const interval = setInterval(() => {
      setPosition(getRandomPosition());
    }, 30000); // Reposition every 30 seconds

    return () => clearInterval(interval);
  }, []);

  return (
    <div
      style={{
        ...styles.watermark,
        top: `${position.top}%`,
        left: `${position.left}%`,
      }}
      data-testid="forensic-watermark"
      aria-hidden="true"
    >
      {id}
    </div>
  );
};

/**
 * Generate a random position within the viewport (10%-80% range
 * to avoid edges where it might be clipped).
 */
function getRandomPosition(): { top: number; left: number } {
  return {
    top: 10 + Math.random() * 70,
    left: 10 + Math.random() * 70,
  };
}

const styles: Record<string, React.CSSProperties> = {
  watermark: {
    position: 'absolute',
    pointerEvents: 'none',
    userSelect: 'none',
    fontSize: 12,
    fontFamily: 'monospace',
    color: 'rgba(255, 255, 255, 0.02)',
    opacity: 1,
    zIndex: 40,
    whiteSpace: 'nowrap',
    letterSpacing: '0.5px',
  },
};
